import { useContext, useReducer } from "react";
import RqContext from "./RqContext";
import RqReducer from "./RqReducer";

import { trackPromise } from "react-promise-tracker";
import { useToasts } from "react-toast-notifications";
import authToken from "../../Config/AuthToken";
import axiosClient from "../../Config/Axios";
import axiosClientFiles from "../../Config/AxiosFiles";
import i18n from "../../i18n";
import {
  DELETE_REQUEST,
  GET_REQUEST,
  POST_REQUEST,
  PUT_REQUEST,
  REQUEST_FAILED,
} from "../../Store/actions";
import AuthContext from "../Auth/AuthContext";

let requestInterceptor, responseInterceptor, refreshingTokenPromise;

const RqState = (props) => {
  const { addToast } = useToasts();

  const ROLE_TO_API_URL = {
    4: "/manufacturer",
    6: "/distributor",
    5: "/brandmanager",
    11: "/customerserviceadvisor",
    0: "unauthenticated",
  };

  const getBaseUrl = (url) => {
    if (url == undefined) {
      const profileData = JSON.parse(localStorage.getItem("profile"));
      const activeRole = profileData?.profilesList?.[0] || 0;
      return ROLE_TO_API_URL[activeRole.toLowerCase()] || "/unauthenticated";
    }
    return url;
  };

  const initialState = {
    state: false,
    data: null,
    message: null,
  };

  const { refreshLogin } = useContext(AuthContext);
  const [state, dispatch] = useReducer(RqReducer, initialState);

  if (requestInterceptor === undefined) {
    requestInterceptor = axiosClient.interceptors.request.use(
      (config) => {
        const targetDomain = process.env.REACT_APP_S3_BUCKET;
        const clientId = process.env.REACT_APP_CLIENT_SECRET;

        // Construimos la URL completa que Axios va a llamar
        // (Axios a veces separa baseURL y url, así que unimos ambas por seguridad)
        const fullRequestUrl = `${config.baseURL || ""}${config.url || ""}`;
        // SI tenemos las variables de entorno Y la petición va hacia nuestro S3/CloudFront...
        if (targetDomain && clientId && fullRequestUrl.includes(targetDomain)) {
          // ...entonces inyectamos el header para pasar el WAF
          config.headers["x-tomalo-client-id"] = clientId;
        }
        return config;
      },
      (err) => {
        console.log("rq_err", err);
        return Promise.reject(err);
      },
    );
  }

  if (responseInterceptor === undefined) {
    responseInterceptor = axiosClient.interceptors.response.use(
      function (response) {
        console.log("rq_response", response.status);
        return response;
      },
      async (error) => {
        let res = error.response;
        const originalRequest = error.config;
        console.log("er_response", res);

        // 1. Identificar si la petición iba hacia S3/CloudFront
        const targetDomain = process.env.REACT_APP_S3_BUCKET;
        const fullRequestUrl = `${originalRequest.baseURL || ""}${
          originalRequest.url || ""
        }`;
        const isS3Request =
          targetDomain && fullRequestUrl.includes(targetDomain);
        if (
          (res?.status === 401 || res?.status === 403) &&
          !isS3Request &&
          !originalRequest._retry
        ) {
          console.log("Refreshing token");
          originalRequest._retry = true; // Marcamos para no volver a entrar aquí con la misma petición

          if (refreshingTokenPromise === undefined) {
            refreshingTokenPromise = refreshLogin();
          }

          await refreshingTokenPromise;
          refreshingTokenPromise = undefined;

          const { bearer_token, access_token } = getLoggedUser();
          originalRequest.headers["Authorization"] = "Bearer " + bearer_token;
          originalRequest.headers["access_token"] = access_token;

          return axiosClient(originalRequest);
        } else if (res?.status !== 200 && !isS3Request) {
          console.log("responseInterceptor", res);
          try {
            if (typeof res?.data?.message === "string") {
              addToast(res.data.message, {
                appearance: "error",
                autoDismiss: true,
                placement: "bottom-right",
              });
            }
          } catch (e) {
            console.log("Cant show the error, here is", res?.data);
          }
        }
        return Promise.reject(error);
      },
    );
  }

  // Login user on API
  const requestGET = async (data) => {
    const { url, json, fun, save = () => {} } = data;
    try {
      const newUrl = getBaseUrl(url);
      getLoggedUser();
      const response = await trackPromise(
        axiosClient.post(newUrl, json, { params: { function: fun } }),
      );
      dispatch({ type: GET_REQUEST, payload: response.data });
      save(response.data);
    } catch (error) {
      let res = error.response;
      if (res.status == 500) {
        try {
          if (typeof res.data.message === "string") {
            addToast(res.data.message, {
              appearance: "error",
              autoDismiss: true,
              placement: "bottom-right",
            });
          }
        } catch (error) {
          console.log("Cant show the error, here is", res.data);
        }
      }
      dispatch({
        type: REQUEST_FAILED,
      });
    }
  };

  const requestPOST = async (data, message = i18n.t("POST_REQUEST")) => {
    const { url, json, fun, save = () => {} } = data;
    try {
      const newUrl = getBaseUrl(url);
      getLoggedUser();
      const response = await trackPromise(
        axiosClient.post(newUrl, json, { params: { function: fun } }),
      );
      dispatch({ type: POST_REQUEST, payload: response.data });
      save(response.data);
      addToast(message, {
        appearance: "success",
        autoDismiss: true,
        placement: "bottom-right",
      });
    } catch (error) {
      console.log(error);
      let res = error.response;
      if (res) {
        if (res.status == 500) {
          try {
            if (typeof res.data.message === "string") {
              addToast(res.data.message, {
                appearance: "error",
                autoDismiss: true,
                placement: "bottom-right",
              });
            }
          } catch (error) {
            console.log("Cant show the error, here is", res.data);
          }
        }
        dispatch({
          type: REQUEST_FAILED,
        });
      }
      save(null);
      dispatch({
        type: REQUEST_FAILED,
      });
    }
  };

  const requestPUT = async (data) => {
    const { url, json, fun, save = () => {} } = data;
    try {
      const newUrl = getBaseUrl(url);
      getLoggedUser();
      const response = await trackPromise(
        axiosClient.post(newUrl, json, { params: { function: fun } }),
      );
      dispatch({ type: PUT_REQUEST, payload: response.data });
      save(response.data);
      addToast(i18n.t("PUT_REQUEST"), {
        appearance: "success",
        autoDismiss: true,
        placement: "bottom-right",
      });
    } catch (error) {
      let res = error.response;
      if (res.status == 500) {
        try {
          if (typeof res.data.message === "string") {
            addToast(res.data.message, {
              appearance: "error",
              autoDismiss: true,
              placement: "bottom-right",
            });
          }
        } catch (error) {
          console.log("Cant show the error, here is", res.data);
        }
      }
      save(null);
      dispatch({
        type: REQUEST_FAILED,
      });
    }
  };

  const requestModel = async (data) => {
    const { url, json, save } = data;
    try {
      const response = await trackPromise(axiosClientFiles.put(url, json));
      dispatch({ type: PUT_REQUEST, payload: response.data });
      save(response.data);
    } catch (error) {
      addToast(error.message, {
        appearance: "error",
        autoDismiss: true,
        placement: "bottom-right",
      });
      dispatch({
        type: REQUEST_FAILED,
      });
    }
  };

  const requestDELETE = async (data) => {
    const { url, json, fun, save = () => {} } = data;
    try {
      const newUrl = getBaseUrl(url);

      getLoggedUser();
      const response = await trackPromise(
        axiosClient.post(newUrl, json, { params: { function: fun } }),
      );
      dispatch({ type: DELETE_REQUEST, payload: response.data });
      save(response.data);
      addToast(i18n.t("DELETE_REQUEST"), {
        appearance: "success",
        autoDismiss: true,
        placement: "bottom-right",
      });
      if (save) {
        save(response.data);
      }
    } catch (error) {
      addToast(error.message, {
        appearance: "error",
        autoDismiss: true,
        placement: "bottom-right",
      });
      save(null);
      dispatch({
        type: REQUEST_FAILED,
      });
    }
  };

  // Get data of current user
  const getLoggedUser = () => {
    const bearer_token = localStorage.getItem("bearer_token");
    const access_token = localStorage.getItem("access_token");
    if (bearer_token) {
      authToken(bearer_token, access_token);
    }
    return { bearer_token, access_token };
  };

  return (
    <RqContext.Provider
      value={{
        requestData: state.data,
        requestState: state.state,
        requestMessage: state.message,
        requestGET,
        requestPOST,
        requestPUT,
        requestDELETE,
        requestModel,
      }}
    >
      {props.children}
    </RqContext.Provider>
  );
};

export default RqState;
